import {AddSchema, CreateAccessor, GetDoc, GetDocs, MGLClass} from "mobx-graphlink";
import {emptyArray} from "js-vextensions";
import {BulletEventType, BulletFilterReason} from "../../../EchoHunt/Types.js";

export const GetEchoCorpusBullet = CreateAccessor((id: string|n): EchoCorpusBullet|n=>{
	if (id == null) return null;
	return GetDoc({}, a=>a.echoCorpusBullets.get(id));
});
export const GetEchoCorpusBullets = CreateAccessor((userID: string|n): EchoCorpusBullet[]=>{
	if (userID == null) return emptyArray; // if we aren't signed-in, don't even try to make request (the security-rules will make it fail)
	return GetDocs({params: {filter: {creator: userID && {equalTo: userID}}}}, a=>a.echoCorpusBullets);
});
export const GetEchoCorpusBulletsForDream = CreateAccessor((dreamID: string|n): EchoCorpusBullet[]=>{
	if (dreamID == null) return emptyArray;
	return GetDocs({params: {filter: {dreamId: {equalTo: dreamID}}}}, a=>a.echoCorpusBullets);
});

/** One row per journal bullet (§14 `corpus_bullet`). Cached so the index pass (§4) only runs once
 * per bullet; `sourceTextHash` lets an edited journal bullet invalidate just its own row. */
@MGLClass({table: "echoCorpusBullets"})
export class EchoCorpusBullet {
	constructor(data?: Partial<EchoCorpusBullet>) { Object.assign(this, data); }

	id: string;
	creator: string;
	createdAt: number;

	/** `entryId/segmentId` of the dream this bullet came from (§4.1) */
	dreamId: string;
	/** hash of the raw bullet text, so edits invalidate rather than silently mismatch (§16.2 P4) */
	sourceTextHash: string;

	/** `entryId/segmentId/<ordinal of the top-level bullet>` (§16.2 P4) */
	bulletId?: string|n;
	/** the journal's own date for the parent dream */
	date?: number|n;
	/** the `# ` heading / scene the bullet sat under (§4.1) */
	heading?: string|n;
	/** verbatim text from the journal */
	rawText?: string|n;
	/** standalone rewrite in house style, references to sibling bullets resolved (§4.2, §4.3) */
	selfContainedText?: string|n;

	eventType?: BulletEventType|n;
	/** how many out-of-place objects the bullet's core contains; used to match fake seeding (§4.1, §5.2) */
	oddityCount?: number|n;

	/** why the bullet is excluded from the usable pool; null means it is usable (§4.4) */
	filterReason?: BulletFilterReason|n;
	/** `CheckBullet` violations that survived the post-pass; shown in the Corpus tab so a
	 * `styleViolation` verdict is explainable rather than mysterious (P5) */
	postPassViolations?: string[]|n;
	/** when the index pass last wrote this row (P5) */
	indexedAt?: number|n;
	/** embedding vector for the typicality cap / theme seeding; backfilled in P12 (§4.1) */
	embedding?: number[]|n;

	/** how many rounds have *shown* this bullet (§4.5) */
	exposureRoundCount?: number|n;
	/** how many times the user actually saw it at reveal; weighted above in-round (§4.5) */
	exposureRevealCount?: number|n;
	lastUsed?: number|n;
}
AddSchema("EchoCorpusBullet", {
	properties: {
		id: {type: "string"},
		creator: {type: "string"},
		createdAt: {type: "number"},

		dreamId: {type: "string"},
		sourceTextHash: {type: "string"},

		bulletId: {type: ["null", "string"]},
		date: {type: ["null", "number"]},
		heading: {type: ["null", "string"]},
		rawText: {type: ["null", "string"]},
		selfContainedText: {type: ["null", "string"]},

		eventType: {oneOf: [{type: "null"}, {$ref: "BulletEventType"}]},
		oddityCount: {type: ["null", "number"]},

		filterReason: {oneOf: [{type: "null"}, {$ref: "BulletFilterReason"}]},
		postPassViolations: {oneOf: [{type: "null"}, {items: {type: "string"}}]},
		indexedAt: {type: ["null", "number"]},
		embedding: {oneOf: [{type: "null"}, {items: {type: "number"}}]},

		exposureRoundCount: {type: ["null", "number"]},
		exposureRevealCount: {type: ["null", "number"]},
		lastUsed: {type: ["null", "number"]},
	},
});
